import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Divider,
  Stack,
  Checkbox,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import SearchIcon from "@mui/icons-material/Search";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import axios from "axios";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { buildApiUrl } from "./apiConfig";
import {
  INVESTITII_PRODUCT_DELETED_EVENT,
  normalizeInvestitiiSkuKey,
} from "./investitiiEvents";

const notifiedCreatedTasks = new Set();
let createdNotificationsInitialized = false;
const AUTH_STATUS_REFRESH_INTERVAL_MS = 5000;

const stripHtmlTags = (value = "") =>
  value
    .replace(/<\/?[^>]+(>|$)/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const getAttachmentFilename = (attachment = {}) =>
  String(attachment.filename || "").toLowerCase();

const hasContractAttachment = (attachments = []) =>
  attachments.some((att) => {
    const filename = getAttachmentFilename(att);
    return (
      (filename.startsWith("contract_") ||
        /^(lp00[1-9]|lp01[0-8])/.test(filename)) &&
      filename.endsWith(".pdf")
    );
  });

const hasQualityCheckAttachment = (attachments = []) =>
  attachments.some((att) => getAttachmentFilename(att).includes("qualitycheck"));

const hasRespinsAttachment = (attachments = []) =>
  attachments.some((att) => {
    const filename = String(att.filename || "");
    return filename.includes("_Nota_Retur_") || filename.includes("_Nota_fk_");
  });

const normalizeJiraFieldValue = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return value.value || value.name || value.id || "";
  }
  return String(value);
};

const formatPhotoFilename = (rawName = "", fallback = "foto") => {
  const noHtml = stripHtmlTags(rawName);
  const withoutLeadingDigits =
    noHtml.replace(/^\s*\d{5}\s*/, "").trim() || noHtml;
  const normalized = withoutLeadingDigits
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .replace(/[^A-Za-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, "_");
  const base = normalized || fallback;
  return `${base}_2`;
};

const TaskTable = ({
  onTaskSelect,
  projectKey,
  status,
  reload,
  selectedTaskId,
  hiddenTaskIds = [],
}) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [customFieldOptions, setCustomFieldOptions] = useState({});
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showStudioProgramare, setShowStudioProgramare] = useState(false);
  const [studioSessions, setStudioSessions] = useState([]);
  const [studioLoading, setStudioLoading] = useState(false);
  const [studioError, setStudioError] = useState("");
  const [sessionDrafts, setSessionDrafts] = useState({});
  const [studioCopyInfo, setStudioCopyInfo] = useState({
    open: false,
    message: "",
  });
  const [copiedSku, setCopiedSku] = useState(null);
  const copyAnimationTimeoutRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const shouldRestoreScrollRef = useRef(false);
  const tasksRef = useRef([]);
  const authRefreshInFlightRef = useRef(false);
  const authRefreshTokenRef = useRef(0);
  const [expandedSkus, setExpandedSkus] = useState({});
  const [newTaskInputs, setNewTaskInputs] = useState({});

  useEffect(() => {
    tasksRef.current = tasks;
  }, [tasks]);

  const buildPhotoFilename = useCallback((skuItem) => {
    const baseName =
      skuItem?.summary || skuItem?.product_name || skuItem?.sku || "foto";
    return formatPhotoFilename(baseName, skuItem?.sku || "foto");
  }, []);

  const handleSkuPhotoFilename = useCallback(
    (skuItem) => {
      const fileName = buildPhotoFilename(skuItem);

      const notify = () =>
        setStudioCopyInfo({
          open: true,
          message: `Numele ${fileName} a fost generat și copiat.`,
        });

      const canCopy =
        typeof navigator !== "undefined" && navigator?.clipboard?.writeText;

      const finalize = () => {
        notify();
        setCopiedSku(skuItem.sku);
        if (copyAnimationTimeoutRef.current) {
          clearTimeout(copyAnimationTimeoutRef.current);
        }
        copyAnimationTimeoutRef.current = setTimeout(() => {
          setCopiedSku(null);
        }, 1800);
      };

      if (canCopy) {
        navigator.clipboard
          .writeText(fileName)
          .then(finalize)
          .catch(() => {
            window.prompt("Nume fișier generat", fileName);
            finalize();
          });
      } else {
        window.prompt("Nume fișier generat", fileName);
        finalize();
      }
    },
    [buildPhotoFilename]
  );

  const closeStudioCopyInfo = useCallback(() => {
    setStudioCopyInfo((prev) => ({ ...prev, open: false }));
  }, []);

  useEffect(
    () => () => {
      if (copyAnimationTimeoutRef.current) {
        clearTimeout(copyAnimationTimeoutRef.current);
      }
    },
    []
  );
  const getChipColor = (status) => {
    switch (status) {
      case "Nepreluat":
        return "default"; // Gri
      case "Preluat":
        return "warning"; // Portocaliu
      case "Acceptat":
        return "success"; // Verde
      case "Anulat":
        return "error"; // Roșu
      default:
        return "default"; // Fallback pentru necunoscut
    }
  };

  const formatSummaryForDisplay = (summary = "") => {
    if (!summary) return "";
    const [firstToken, ...rest] = summary.split(" ");
    if (rest.length === 0) {
      return firstToken;
    }
    return `${firstToken}…`;
  };

  const isStocExternDa = (value) => {
    if (value == null) return false;
    return (
      value
        .toString()
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") === "da"
    );
  };

  const fetchWorkdaysDiff = async (summary) => {
    try {
      const response = await axios.get(
        buildApiUrl(`/api/tasks/prod-status/${encodeURIComponent(summary)}`)
      );
      const { dateCreated } = response.data;

      if (!dateCreated) return 0; // Dacă `dateCreated` nu există, returnăm 0

      const calculateWorkdays = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Verificăm dacă startDate este după endDate
        if (start > end) {
          console.warn(`Start date ${startDate} is after end date ${endDate}`);
          return 0; // Returnăm 0 pentru cazuri anormale
        }

        let count = 0;

        while (start <= end) {
          const day = start.getDay();
          if (day !== 0 && day !== 6) {
            // Exclude sâmbătă și duminică
            count++;
          }
          start.setDate(start.getDate() + 1);
        }

        return count - 1; // Excludem ziua curentă din calcul
      };

      const workdays = calculateWorkdays(dateCreated, new Date());
      return workdays < 0 ? 0 : workdays; // Ne asigurăm că rezultatul nu e negativ
    } catch (error) {
      console.error(
        `Error fetching workdays difference for summary ${summary}:`,
        error
      );
      return 0; // În cazul unei erori, returnăm 0
    }
  };

  const fetchCustomFieldAndAwbStatus = async (tasks) => {
    const updatedTasks = await Promise.all(
      tasks.map(async (task) => {
        try {
          const response = await axios.get(
            buildApiUrl(`/api/tasks/jira-task/${task.issue_id}`)
          );
          const awbNumber = response.data.customfield_10163;
          let awbStatusGls = "Unknown";

          if (awbNumber) {
            awbStatusGls = await fetchAwbStatus(awbNumber);
          }

          const contractValid = hasContractAttachment(response.data.attachments);
          return {
            ...task,
            awbStatusGls,
            contractValid,
            awbNumber,
          };
        } catch (error) {
          console.error("Error fetching AWB status:", error);
          return task;
        }
      })
    );
    return updatedTasks;
  };

  const fetchAwbStatus = async (awbNumber) => {
    try {
      const response = await axios.post(
        buildApiUrl("/api/tasks/get-awb-status"),
        { awbNumber }
      );

      if (response.data && response.data.statusDescription) {
        return response.data.statusDescription;
      } else {
        return "Status not found";
      }
    } catch (error) {
      console.error("Error fetching AWB status from server:", error);
      return "Error fetching status";
    }
  };

  const fetchQCValid = async (task, jiraTaskData = null) => {
    try {
      const jiraData =
        jiraTaskData ||
        (
          await axios.get(
            buildApiUrl(`/api/tasks/jira-task/${task.issue_id}`)
          )
        ).data;
      const attachments = Array.isArray(jiraData.attachments)
        ? jiraData.attachments
        : [];
      const hasQualityCheck = hasQualityCheckAttachment(attachments);
      const summary = jiraData.summary || task.summary || "";
      const prodStatusResponse = await axios.get(
        buildApiUrl(
          `/api/tasks/prod-status-qcvalid/${encodeURIComponent(summary)}`
        )
      );

      const {
        condition,
        description,
        selectedTags = [],
      } = prodStatusResponse.data;

      let qcStatus;

      if (
        hasQualityCheck &&
        condition &&
        description &&
        selectedTags.length > 0
      ) {
        qcStatus = "complete";
      } else if (
        hasQualityCheck ||
        condition ||
        description ||
        selectedTags.length > 0
      ) {
        qcStatus = "partial";
      } else {
        qcStatus = "not_available";
      }

      return qcStatus;
    } catch (error) {
      console.error(
        `Error fetching QC validation for task ${task.issue_id}:`,
        error
      );
      return "not_available";
    }
  };

  const fetchCiornaValid = async (task, jiraTaskData = null) => {
    try {
      let potentialSku = normalizeJiraFieldValue(jiraTaskData?.customfield_10083);
      if (!potentialSku) {
        console.log(
          `🔍 [Ciorna] Fetching SKU from leb-customfield for task: ${task.issue_id}`
        );
        const customFieldResponse = await axios.get(
          buildApiUrl(`/api/tasks/leb-customfield/${task.issue_id}`)
        );
        potentialSku = customFieldResponse.data.customFieldValue || "";
      }
      potentialSku = potentialSku.trim(); // eliminăm spațiile goale

      console.log(
        `📦 [Ciorna] Potential SKU received and cleaned: "${potentialSku}"`
      );

      if (potentialSku) {
        console.log(
          `🌐 [Ciorna] Calling fetchWooCommerceSKU for: "${potentialSku}"`
        );
        const wooCommerceResult = await fetchWooCommerceSKU(potentialSku);
        console.log(`✅ [Ciorna] WooCommerce SKU result:`, wooCommerceResult);
        return wooCommerceResult;
      } else {
        console.warn(
          `⚠️ [Ciorna] No valid SKU found in leb-customfield for task: ${task.issue_id}`
        );
      }

      return false;
    } catch (error) {
      console.error(
        `❌ [Ciorna] Error during validation for task ${task.issue_id}:`,
        error
      );
      return false;
    }
  };

  const fetchRespinsValid = async (task, jiraTaskData = null) => {
    try {
      const jiraData =
        jiraTaskData ||
        (
          await axios.get(
            buildApiUrl(`/api/tasks/jira-task/${task.issue_id}`)
          )
        ).data;
      let customFieldValue = jiraData.customfield_10188;
      if (customFieldValue === undefined) {
        const customFieldValueResponse = await axios.get(
          buildApiUrl(`/api/tasks/customfield-value-qcstatus/${task.issue_id}`)
        );
        customFieldValue = customFieldValueResponse.data.customFieldValue;
      }

      let respinsValid = "not available";
      const attachments = Array.isArray(jiraData.attachments)
        ? jiraData.attachments
        : [];
      const hasRelevantAttachment = hasRespinsAttachment(attachments);

      const hasRespinsOrFake =
        customFieldValue &&
        (customFieldValue.includes("Respins") ||
          customFieldValue.includes("Fake"));

      if (hasRelevantAttachment) {
        respinsValid = "green";
      } else if (hasRespinsOrFake) {
        respinsValid = "orange";
      }

      return respinsValid;
    } catch (error) {
      console.error(
        `Error fetching Respins validation for task ${task.issue_id}:`,
        error
      );
      return "not available";
    }
  };

  const fetchProdStatus = async (task) => {
    try {
      const prodResponse = await axios.get(
        buildApiUrl(
          `/api/tasks/prod-status-studiovalid/${encodeURIComponent(
            task.summary || ""
          )}`
        )
      );
      const prodStatus = prodResponse.data.status;
      const isPreCheck = prodStatus === "11_PRE-CHECK";
      const fisaValidInPROD = ["CHECK", "CIORNA", "ONLINE"].includes(
        prodStatus
      );
      const studioValid = prodResponse.data.studioValid;

      return {
        fisaValid: fisaValidInPROD,
        studioValid,
        isPreCheck,
        prodStatusName: prodStatus,
      };
    } catch (error) {
      console.error(
        `Error fetching PROD status for task ${task.issue_id}:`,
        error
      );
      return {
        fisaValid: false,
        studioValid: false,
        isPreCheck: false,
        prodStatusName: "",
      };
    }
  };

  const applyTaskPatch = useCallback((issueId, patch) => {
    setTasks((prevTasks) =>
      prevTasks.map((task) =>
        task.issue_id === issueId ? { ...task, ...patch } : task
      )
    );
  }, []);

  const refreshAuthenticationColumns = useCallback(
    async (sourceTasks = [], { force = false } = {}) => {
      if (status !== "AUTENTIFICARE") {
        return;
      }
      const rows = Array.isArray(sourceTasks) ? sourceTasks : [];
      if (!rows.length) {
        return;
      }
      if (authRefreshInFlightRef.current && !force) {
        return;
      }

      const refreshToken = authRefreshTokenRef.current + 1;
      authRefreshTokenRef.current = refreshToken;
      authRefreshInFlightRef.current = true;
      const skuByIssueId = {};

      try {
        await Promise.all(
          rows.map(async (task) => {
            try {
              const detailsResponse = await axios.get(
                buildApiUrl(`/api/tasks/jira-task/${task.issue_id}`)
              );
              const jiraData = detailsResponse.data || {};
              const attachments = Array.isArray(jiraData.attachments)
                ? jiraData.attachments
                : [];
              const hasQualityCheck = hasQualityCheckAttachment(attachments);
              const contractValid = hasContractAttachment(attachments);
              const sku = normalizeJiraFieldValue(jiraData.customfield_10083);
              if (sku) {
                skuByIssueId[task.issue_id] = sku;
              }

              const workdaysPromise = hasQualityCheck
                ? Promise.resolve(0)
                : fetchWorkdaysDiff(task.summary);

              const [qcValid, ciornaValid, respinsValid, prodStatus, workdaysDiff] =
                await Promise.all([
                  fetchQCValid(task, jiraData),
                  fetchCiornaValid(task, jiraData),
                  fetchRespinsValid(task, jiraData),
                  fetchProdStatus(task),
                  workdaysPromise,
                ]);

              if (authRefreshTokenRef.current !== refreshToken) {
                return;
              }

              applyTaskPatch(task.issue_id, {
                qcValid,
                ciornaValid,
                respinsValid,
                fisaValid: prodStatus.fisaValid,
                studioValid: prodStatus.studioValid,
                isPreCheck: prodStatus.isPreCheck,
                prodStatusName: prodStatus.prodStatusName,
                contractValid,
                investitiiSku: sku,
                workdaysDiff,
                hasQualityCheck,
                customfield_10185: jiraData.customfield_10185,
              });
            } catch (taskError) {
              console.error(
                `Error refreshing authentication status for ${task.issue_id}:`,
                taskError
              );
            }
          })
        );

        const skuList = Object.values(skuByIssueId).filter(Boolean);
        if (!skuList.length || authRefreshTokenRef.current !== refreshToken) {
          return;
        }

        try {
          const studioResponse = await axios.post(
            buildApiUrl("/api/tasks/pozestudio/status-by-skus"),
            { skus: skuList }
          );
          const studioStatusMap = studioResponse.data?.map || {};
          if (authRefreshTokenRef.current !== refreshToken) {
            return;
          }
          setTasks((prevTasks) =>
            prevTasks.map((task) => {
              const sku = skuByIssueId[task.issue_id];
              if (!sku) {
                return task;
              }
              return {
                ...task,
                studioDone: studioStatusMap[sku] === "DA",
              };
            })
          );
        } catch (studioError) {
          console.error("Error fetching studio status map:", studioError);
        }

        try {
          const investitiiResponse = await axios.post(
            buildApiUrl("/api/investitii/products/status-by-skus"),
            { skus: skuList }
          );
          const investitiiStatusMap = investitiiResponse.data?.map || {};
          if (authRefreshTokenRef.current !== refreshToken) {
            return;
          }
          setTasks((prevTasks) =>
            prevTasks.map((task) => {
              const sku = skuByIssueId[task.issue_id];
              if (!sku) {
                return task;
              }
              const investmentProduct =
                investitiiStatusMap[normalizeInvestitiiSkuKey(sku)] || null;
              return {
                ...task,
                investitiiFinantat: Boolean(investmentProduct?.assigned),
                investitiiProduct: investmentProduct,
              };
            })
          );
        } catch (investitiiError) {
          console.error("Error fetching investitii status map:", investitiiError);
        }
      } finally {
        if (authRefreshTokenRef.current === refreshToken) {
          authRefreshInFlightRef.current = false;
        }
      }
    },
    [
      applyTaskPatch,
      fetchCiornaValid,
      fetchProdStatus,
      fetchQCValid,
      fetchRespinsValid,
      fetchWorkdaysDiff,
      status,
    ]
  );

  const fetchAuthenticationTasks = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        buildApiUrl(`/api/tasks/jira-tasks/${projectKey}/${status}`)
      );

      const baseTasks = response.data.map((task) => ({
        ...task,
        expiraStatus: calculateExpirationStatus(task.customfield_10078),
      }));
      setTasks(baseTasks);
      setLoading(false);

      if (status === "AUTENTIFICARE") {
        refreshAuthenticationColumns(baseTasks, { force: true });
        return;
      } else if (status === "TRANZIT") {
        const updatedTasks = await fetchCustomFieldAndAwbStatus(baseTasks);
        setTasks(updatedTasks);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWooCommerceSKU = async (sku) => {
    if (sku) {
      const url = buildApiUrl(
        `/api/tasks/check-sku-in-woo/${encodeURIComponent(sku)}`
      );

      try {
        const response = await axios.get(url);
        return response.data.exists;
      } catch (error) {
        console.error(
          "Error fetching SKU from backend WooCommerce proxy:",
          error
        );
        return false;
      }
    }
    return false;
  };

  const handleRowClick = (task) => {
    onTaskSelect(task.issue_id);
  };

  const handleExportPDF = async () => {
    try {
      const tasksToExport = tasks.filter(
        (task) => !task.qcValid || task.qcValid !== "complete"
      );

      const response = await axios.post(
        "https://server-9p4hx.kinsta.app/api/tasks/export-pdf-qc",
        {
          tasks: tasksToExport,
        },
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "Tasks_QC_Invalid.pdf";
      link.click();
    } catch (error) {
      console.error("Error exporting to PDF:", error);
    }
  };

  const calculateExpirationStatus = (expirationDate) => {
    if (!expirationDate) {
      return "Fără dată";
    }

    const today = new Date().setHours(0, 0, 0, 0); // Data curentă (ora 00:00)
    const expDate = new Date(expirationDate).setHours(0, 0, 0, 0); // Data expirării

    if (expDate < today) {
      return "Expirat"; // Roșu
    } else if (expDate === today) {
      return "Astăzi"; // Portocaliu
    } else {
      return "Curând"; // Verde
    }
  };

  useEffect(() => {
    if (status === "AUTENTIFICARE" || status === "TRANZIT") {
      return;
    }

    const fetchTasks = async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          buildApiUrl(`/api/tasks/jira-tasks/${projectKey}/${status}`)
        );

        // Log pentru a verifica răspunsul
        console.log("Fetched tasks:", response.data);

        // Adaugăm expiraStatus pentru fiecare task și folosim direct customfield_10185
        const fetchedTasks = response.data.map((task) => ({
          ...task,
          expiraStatus: calculateExpirationStatus(task.customfield_10078),
        }));

        // Setăm tasks și filteredTasks
        setTasks(fetchedTasks);
        setFilteredTasks(fetchedTasks);
        setLoading(false);
      } catch (error) {
        console.error("Eroare la fetch tasks:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [projectKey, status, reload]); // Dependențe corecte

  useEffect(() => {
    const fetchCustomFieldOptions = async () => {
      try {
        const response = await axios.get(
          buildApiUrl("/api/tasks/jira-custom-field-options/customfield_10185")
        );

        const options = response.data.reduce((acc, option) => {
          acc[option.value] = option.label;
          return acc;
        }, {});

        setCustomFieldOptions((prevOptions) => ({
          ...prevOptions,
          customfield_10185: options,
        }));
      } catch (error) {
        console.error("Eroare la fetch custom field options:", error);
      }
    };

    fetchCustomFieldOptions();
  }, []);

  useEffect(() => {
    const hiddenIdsSet = new Set(hiddenTaskIds);
    setFilteredTasks(
      tasks.filter((task) =>
        !hiddenIdsSet.has(task.issue_id) &&
        task.summary.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, tasks, hiddenTaskIds]);

  useEffect(() => {
    if (status !== "2_CREAT") {
      return;
    }
    if (!createdNotificationsInitialized) {
      tasks.forEach((task) => {
        if (task?.issue_id) {
          notifiedCreatedTasks.add(task.issue_id);
        }
      });
      createdNotificationsInitialized = true;
      return;
    }
    tasks.forEach((task) => {
      if (!task?.issue_id) return;
      if (!notifiedCreatedTasks.has(task.issue_id)) {
        notifiedCreatedTasks.add(task.issue_id);
        window.dispatchEvent(
          new CustomEvent("new-product-created", {
            detail: {
              issueId: task.issue_id,
              summary: task.summary,
            },
          })
        );
      }
    });
  }, [tasks, status]);

  const getRenegotiationQcDetails = async (issueId) => {
    try {
      const response = await axios.get(
        `https://server-9p4hx.kinsta.app/api/qc/renegotiation-qc/${issueId}`
      );
      const { priceOption, newPrice, currentPrice } = response.data;

      setTasks((prevTasks) =>
        prevTasks.map((task) =>
          task.issue_id === issueId
            ? { ...task, priceOption, newPrice, currentPrice }
            : task
        )
      );
    } catch (error) {
      console.error("Error fetching task details:", error);
    }
  };

  // Se asigură că getRenegotiationQcDetails nu e apelat în buclă infinită
  useEffect(() => {
    if (tasks.length > 0) {
      tasks.forEach((task) => {
        if (!task.hasRenegotiationQcDetailsFetched) {
          // Verifică dacă detaliile au fost deja încărcate
          getRenegotiationQcDetails(task.issue_id);
          task.hasRenegotiationQcDetailsFetched = true; // Setăm flag pentru a evita re-fetch
        }
      });
    }
  }, [tasks]);

  useEffect(() => {
    if (status === "AUTENTIFICARE" || status === "TRANZIT") {
      fetchAuthenticationTasks();
    }
  }, [projectKey, status, reload]); // Ascultă modificările status-ului și ale reload-ului

  useEffect(() => {
    if (status !== "AUTENTIFICARE") {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      if (document.hidden) {
        return;
      }
      refreshAuthenticationColumns(tasksRef.current);
    }, AUTH_STATUS_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [refreshAuthenticationColumns, status]);

  useEffect(() => {
    if (status !== "AUTENTIFICARE") {
      return undefined;
    }

    const handleInvestitiiProductDeleted = (event) => {
      const deletedSku = normalizeInvestitiiSkuKey(event.detail?.sku);
      if (!deletedSku) return;

      setTasks((prevTasks) =>
        prevTasks.map((task) => {
          const taskSku = normalizeInvestitiiSkuKey(
            task.investitiiSku ||
              task.investitiiProduct?.sku ||
              task.customfield_10083 ||
              task.sku,
          );
          if (taskSku !== deletedSku) {
            return task;
          }

          return {
            ...task,
            investitiiFinantat: false,
            investitiiProduct: null,
          };
        }),
      );
      refreshAuthenticationColumns(tasksRef.current, { force: true });
    };

    window.addEventListener(
      INVESTITII_PRODUCT_DELETED_EVENT,
      handleInvestitiiProductDeleted,
    );

    return () =>
      window.removeEventListener(
        INVESTITII_PRODUCT_DELETED_EVENT,
        handleInvestitiiProductDeleted,
      );
  }, [refreshAuthenticationColumns, status]);

  useEffect(() => {
    const fetchWorkdaysForTasks = async () => {
      try {
        const updatedTasks = await Promise.all(
          tasks.map(async (task) => {
            if (task.summary && task.workdaysDiff === undefined) {
              const workdaysDiff = await fetchWorkdaysDiff(task.summary);
              return { ...task, workdaysDiff };
            }
            return task;
          })
        );
        setTasks(updatedTasks);
      } catch (error) {
        console.error("Error fetching workdays for tasks:", error);
      }
    };

    if (tasks.length > 0) {
      fetchWorkdaysForTasks();
    }
  }, [projectKey, status]); // Eliminăm dependența `tasks` pentru a preveni bucle infinite

  const loadStudioSessions = useCallback(async () => {
    try {
      setStudioLoading(true);
      setStudioError("");
      const response = await axios.get(
        "https://server-9p4hx.kinsta.app/api/tasks/pozestudio/sesiuni-cu-taskuri"
      );
      const data = response.data?.sessions || [];
      const grouped = data.reduce((acc, session) => {
        const key = `${session.fotograf || ""}-${session.sesiune_nr || ""}`;
        if (!acc[key]) {
          acc[key] = {
            sesiune_code: session.sesiune_code,
            fotograf: session.fotograf,
            sesiune_nr: session.sesiune_nr,
            created_at: session.created_at,
            updated_at: session.updated_at,
            data_sesiune: session.data_sesiune,
            finalizat: session.finalizat,
            skus: [],
          };
        }
        acc[key].skus.push(
          ...(session.skus || []).map((sku) => ({
            ...sku,
            tasks: Array.isArray(sku.tasks) ? sku.tasks : [],
          }))
        );
        return acc;
      }, {});
      const filteredSessions = Object.values(grouped).filter(
        (session) =>
          !(session.finalizat && session.finalizat.toUpperCase() === "DA")
      );
      const sortedSessions = filteredSessions.sort((a, b) => {
        const dateA = a.data_sesiune
          ? new Date(a.data_sesiune).getTime()
          : Infinity;
        const dateB = b.data_sesiune
          ? new Date(b.data_sesiune).getTime()
          : Infinity;
        if (dateA === dateB) {
          return (a.created_at || "").localeCompare(b.created_at || "");
        }
        return dateA - dateB;
      });
      setStudioSessions(sortedSessions);
    } catch (error) {
      console.error("[StudioProgramare] sessions fetch error", error);
      setStudioError("Nu am putut încărca sesiunile.");
    } finally {
      setStudioLoading(false);
    }
  }, []);

  const handleSessionDateChange = (code, value) => {
    setSessionDrafts((prev) => ({
      ...prev,
      [code]: value,
    }));
  };

  const preserveScrollPosition = () => {
    if (typeof window !== "undefined") {
      scrollPositionRef.current =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop ||
        0;
      shouldRestoreScrollRef.current = true;
    }
  };

  const restoreScrollPosition = () => {
    if (shouldRestoreScrollRef.current && typeof window !== "undefined") {
      window.scrollTo({
        top: scrollPositionRef.current,
        behavior: "auto",
      });
      shouldRestoreScrollRef.current = false;
    }
  };

  const handleSaveSessionDate = async (session) => {
    const code = session.sesiune_code;
    const draftValue = sessionDrafts[code];
    const dateToSave =
      draftValue ||
      (session.data_sesiune
        ? new Date(session.data_sesiune).toISOString().slice(0, 16)
        : "");
    if (!dateToSave) return;
    try {
      preserveScrollPosition();
      await axios.patch(
        `https://server-9p4hx.kinsta.app/api/tasks/pozestudio/sesiuni/${encodeURIComponent(code)}`,
        {
          dataSesiune: new Date(dateToSave).toISOString(),
        }
      );
      await loadStudioSessions();
    } catch (error) {
      console.error("[StudioProgramare] date save error", error);
      setStudioError("Nu am putut salva data sesiunii.");
    }
  };

  const handleFinalizeSession = async (session) => {
    const code = session.sesiune_code;
    try {
      preserveScrollPosition();
      await axios.patch(
        `https://server-9p4hx.kinsta.app/api/tasks/pozestudio/sesiuni/${encodeURIComponent(code)}`,
        {
          finalizat: "DA",
        }
      );
      await loadStudioSessions();
    } catch (error) {
      console.error("[StudioProgramare] finalize error", error);
      setStudioError("Nu am putut marca sesiunea ca finalizată.");
    }
  };

  const formatDateForInput = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "";
    }
    return date.toISOString().slice(0, 16);
  };

  const handleDeleteSession = async (session) => {
    const code = session.sesiune_code;
    if (!window.confirm(`Ștergi sesiunea ${code}?`)) {
      return;
    }
    try {
      preserveScrollPosition();
      await axios.delete(
        `https://server-9p4hx.kinsta.app/api/tasks/pozestudio/sesiuni/${encodeURIComponent(code)}`
      );
      await loadStudioSessions();
    } catch (error) {
      console.error("[StudioProgramare] delete session error", error);
      setStudioError("Nu am putut șterge sesiunea.");
    }
  };

  const handleDeleteSku = async (session, sku) => {
    const code = session.sesiune_code;
    if (!window.confirm(`Ștergi SKU ${sku} din sesiunea ${code}?`)) {
      return;
    }
    try {
      preserveScrollPosition();
      await axios.delete(
        `https://server-9p4hx.kinsta.app/api/tasks/pozestudio/sesiuni/${encodeURIComponent(code)}/sku/${encodeURIComponent(sku)}`
      );
      await loadStudioSessions();
    } catch (error) {
      console.error("[StudioProgramare] delete sku error", error);
      setStudioError("Nu am putut șterge SKU-ul din sesiune.");
    }
  };

  const updateLocalSkuPozat = useCallback((code, sku, value) => {
    setStudioSessions((prev) =>
      prev.map((sess) => {
        if (sess.sesiune_code !== code) {
          return sess;
        }
        return {
          ...sess,
          skus: (sess.skus || []).map((item) =>
            item.sku === sku ? { ...item, pozat: value } : item
          ),
        };
      })
    );
  }, []);

  const updateLocalSkuTasks = useCallback((code, sku, updater) => {
    setStudioSessions((prev) =>
      prev.map((session) => {
        if (session.sesiune_code !== code) {
          return session;
        }
        const updatedSkus = (session.skus || []).map((item) => {
          if (item.sku !== sku) {
            return item;
          }
          const currentTasks = Array.isArray(item.tasks) ? item.tasks : [];
          const nextTasks =
            typeof updater === "function" ? updater(currentTasks) : updater;
          return { ...item, tasks: nextTasks || [] };
        });
        return { ...session, skus: updatedSkus };
      })
    );
  }, []);

  const toggleSkuDetails = (sku) => {
    setExpandedSkus((prev) => ({
      ...prev,
      [sku]: !prev[sku],
    }));
  };

  const handleTaskInputChange = (sku, value) => {
    setNewTaskInputs((prev) => ({
      ...prev,
      [sku]: value,
    }));
  };

  const handleAddSkuTask = async (session, skuItem) => {
    const code = session.sesiune_code;
    const sku = skuItem?.sku;
    const text = (newTaskInputs[sku] || "").trim();
    if (!code || !sku || !text) {
      return;
    }
    try {
      preserveScrollPosition();
      const response = await axios.post(
        `https://server-9p4hx.kinsta.app/api/tasks/pozestudio/sku-tasks/${encodeURIComponent(sku)}`,
        { text }
      );
      const tasks = response.data?.tasks || [];
      updateLocalSkuTasks(code, sku, tasks);
      setNewTaskInputs((prev) => ({ ...prev, [sku]: "" }));
    } catch (error) {
      console.error("[StudioProgramare] add sku task error", error);
      setStudioError("Nu am putut adăuga task-ul pentru acest SKU.");
    }
  };

  const handleToggleSkuTask = async (session, skuItem, task) => {
    const code = session.sesiune_code;
    const sku = skuItem?.sku;
    if (!code || !sku || !task?.id) {
      return;
    }
    try {
      preserveScrollPosition();
      const response = await axios.patch(
        `https://server-9p4hx.kinsta.app/api/tasks/pozestudio/sku-tasks/${encodeURIComponent(sku)}/${encodeURIComponent(task.id)}`,
        { done: !task.done }
      );
      const tasks = response.data?.tasks || [];
      updateLocalSkuTasks(code, sku, tasks);
    } catch (error) {
      console.error("[StudioProgramare] toggle sku task error", error);
      setStudioError("Nu am putut actualiza statusul task-ului.");
    }
  };

  const handleToggleSkuPozat = async (session, skuItem) => {
    const code = session.sesiune_code;
    if (!code || !skuItem?.sku) {
      return;
    }
    try {
      preserveScrollPosition();
      const nextValue =
        skuItem.pozat && skuItem.pozat.toUpperCase() === "DA" ? "NU" : "DA";
      await axios.patch(
        `https://server-9p4hx.kinsta.app/api/tasks/pozestudio/sesiuni/${encodeURIComponent(code)}/sku/${encodeURIComponent(skuItem.sku)}`,
        { pozat: nextValue }
      );
      updateLocalSkuPozat(code, skuItem.sku, nextValue);
    } catch (error) {
      console.error("[StudioProgramare] mark pozat error", error);
      setStudioError("Nu am putut marca SKU-ul ca pozat.");
    }
  };

  const handleStudioDragEnd = useCallback(
    async (result) => {
      const { destination, source, draggableId } = result;
      if (!destination) {
        return;
      }
      const fromCode = source.droppableId;
      const toCode = destination.droppableId;
      if (!fromCode || !toCode || fromCode === toCode) {
        return;
      }
      setStudioSessions((prev) => {
        const clonedSessions = prev.map((session) => ({
          ...session,
          skus: (session.skus || []).map((sku) => ({
            ...sku,
            tasks: Array.isArray(sku.tasks) ? [...sku.tasks] : [],
          })),
        }));
        const sourceSessionIndex = clonedSessions.findIndex(
          (session) => session.sesiune_code === fromCode
        );
        const destinationSessionIndex = clonedSessions.findIndex(
          (session) => session.sesiune_code === toCode
        );
        if (sourceSessionIndex === -1 || destinationSessionIndex === -1) {
          return prev;
        }
        const sourceSkus = clonedSessions[sourceSessionIndex].skus;
        const destinationSkus = clonedSessions[destinationSessionIndex].skus;
        const [movedItem] = sourceSkus.splice(source.index, 1);
        if (!movedItem) {
          return prev;
        }
        destinationSkus.splice(destination.index, 0, movedItem);
        clonedSessions[sourceSessionIndex] = {
          ...clonedSessions[sourceSessionIndex],
          skus: sourceSkus,
        };
        clonedSessions[destinationSessionIndex] = {
          ...clonedSessions[destinationSessionIndex],
          skus: destinationSkus,
        };
        return clonedSessions;
      });
      try {
        preserveScrollPosition();
        await axios.post(
          "https://server-9p4hx.kinsta.app/api/tasks/pozestudio/sesiuni/muta-sku",
          {
            sku: draggableId,
            fromCode,
            toCode,
          }
        );
      } catch (error) {
        console.error("[StudioProgramare] move sku error", error);
        setStudioError("Nu am putut muta SKU-ul în noua sesiune.");
        await loadStudioSessions();
      }
    },
    [loadStudioSessions]
  );

  useEffect(() => {
    if (!showStudioProgramare) {
      return;
    }
    loadStudioSessions();
  }, [showStudioProgramare, loadStudioSessions]);

  useEffect(() => {
    if (showStudioProgramare) {
      restoreScrollPosition();
    }
  }, [studioSessions, showStudioProgramare]);

  if (showStudioProgramare) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box
          sx={{
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            background: "linear-gradient(135deg, #0f172a, #1d4ed8)",
            borderRadius: 3,
            color: "#fff",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Programare Studio
            </Typography>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<ArrowBackIcon />}
              onClick={() => setShowStudioProgramare(false)}
            >
              Înapoi la tabel
            </Button>
          </Box>
          <Snackbar
            open={studioCopyInfo.open}
            autoHideDuration={2500}
            onClose={closeStudioCopyInfo}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={closeStudioCopyInfo}
              severity="success"
              sx={{ width: "100%" }}
            >
              {studioCopyInfo.message}
            </Alert>
          </Snackbar>
          {studioLoading ? (
            <Typography variant="body1" sx={{ opacity: 0.85 }}>
              Se încarcă sesiunile...
            </Typography>
          ) : studioError ? (
            <Typography variant="body1" sx={{ opacity: 0.85 }}>
              {studioError}
            </Typography>
          ) : (
            <DragDropContext onDragEnd={handleStudioDragEnd}>
              <Box
                sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}
              >
                {studioSessions.map((session) => (
                  <Droppable
                    key={`${session.fotograf}${session.sesiune_nr}`}
                    droppableId={session.sesiune_code}
                  >
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          borderRadius: 3,
                          p: 2.5,
                          background: snapshot.isDraggingOver
                            ? "rgba(59,130,246,0.35)"
                            : "rgba(15,23,42,0.35)",
                          border: "1px solid rgba(255,255,255,0.15)",
                          transition: "background 0.2s ease",
                        }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          spacing={2}
                        >
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            <Typography variant="h6" sx={{ fontWeight: 700 }}>
                              Sesiunea {session.sesiune_nr}{" "}
                              {session.fotograf === "intern"
                                ? "Intern"
                                : "Extern"}
                            </Typography>
                            <Chip
                              size="small"
                              label={session.sesiune_code}
                              sx={{
                                borderRadius: "999px",
                                background:
                                  session.fotograf === "intern"
                                    ? "rgba(34,197,94,0.2)"
                                    : "rgba(249,115,22,0.2)",
                                color:
                                  session.fotograf === "intern"
                                    ? "#BBF7D0"
                                    : "#FED7AA",
                                border:
                                  session.fotograf === "intern"
                                    ? "1px solid rgba(34,197,94,0.5)"
                                    : "1px solid rgba(249,115,22,0.5)",
                              }}
                            />
                            <Chip
                              size="small"
                              label={`${session.skus.length} Produse`}
                              sx={{
                                borderRadius: "999px",
                                background: "rgba(251,146,60,0.25)",
                                color: "#FFEDD5",
                                border: "1px solid rgba(251,146,60,0.4)",
                              }}
                            />
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                              Creată: {session.created_at}
                            </Typography>
                          </Stack>
                          <IconButton
                            color="error"
                            onClick={() => handleDeleteSession(session)}
                            size="small"
                            sx={{ color: "#fecaca" }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                        <Stack
                          direction={{ xs: "column", md: "row" }}
                          spacing={1.5}
                          alignItems={{ xs: "stretch", md: "flex-end" }}
                          sx={{ mt: 1.5 }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              minWidth: 220,
                              maxWidth: 260,
                              width: "100%",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "rgba(255,255,255,0.8)", mb: 0.5 }}
                            >
                              Data sesiune
                            </Typography>
                            <DateTimePicker
                              value={
                                sessionDrafts[session.sesiune_code] ??
                                (session.data_sesiune
                                  ? new Date(session.data_sesiune)
                                  : null)
                              }
                              onChange={(value) =>
                                handleSessionDateChange(
                                  session.sesiune_code,
                                  value
                                )
                              }
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: "small",
                                  sx: {
                                    "& .MuiInputBase-root": {
                                      background: "rgba(15,23,42,0.5)",
                                      color: "#fff",
                                      borderRadius: 2,
                                    },
                                    "& .MuiSvgIcon-root": {
                                      color: "#fff",
                                    },
                                  },
                                },
                                openPickerButton: {
                                  sx: {
                                    color: "#fff",
                                    "& .MuiSvgIcon-root": {
                                      color: "#fff",
                                    },
                                  },
                                },
                              }}
                            />
                          </Box>
                          <Tooltip title="Salvează data">
                            <span>
                              <IconButton
                                color="primary"
                                sx={{
                                  background: "rgba(59,130,246,0.2)",
                                  "&:hover": {
                                    background: "rgba(59,130,246,0.35)",
                                  },
                                  alignSelf: {
                                    xs: "flex-start",
                                    md: "flex-end",
                                  },
                                  mt: { xs: 1, md: 0 },
                                }}
                                onClick={() => handleSaveSessionDate(session)}
                              >
                                <SaveIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Button
                            variant="outlined"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => handleFinalizeSession(session)}
                            disabled={
                              session.finalizat &&
                              session.finalizat.toUpperCase() === "DA"
                            }
                            sx={{
                              textTransform: "none",
                              borderColor: "rgba(34,197,94,0.4)",
                              color: "#BBF7D0",
                              px: 2.5,
                              alignSelf: { xs: "flex-start", md: "flex-end" },
                              mt: { xs: 0.5, md: 0 },
                            }}
                          >
                            {session.finalizat &&
                            session.finalizat.toUpperCase() === "DA"
                              ? "Finalizată"
                              : "Finalizează"}
                          </Button>
                        </Stack>
                        <Box
                          sx={{
                            mt: 1.5,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          {session.skus.length > 0 ? (
                            session.skus.map((skuItem, index) => {
                              const isPozat =
                                skuItem.pozat &&
                                skuItem.pozat.toUpperCase() === "DA";
                              const photoFilename = buildPhotoFilename(skuItem);
                              const isCopied = copiedSku === skuItem.sku;
                              return (
                                <Draggable
                                  draggableId={skuItem.sku}
                                  index={index}
                                  key={skuItem.sku}
                                >
                                  {(draggableProvided, draggableSnapshot) => (
                                    <Box
                                      ref={draggableProvided.innerRef}
                                      {...draggableProvided.draggableProps}
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        gap: 1,
                                        padding: "8px 12px",
                                        background: isPozat
                                          ? "linear-gradient(120deg, rgba(22,163,74,0.35), rgba(34,197,94,0.25))"
                                          : "rgba(15,23,42,0.4)",
                                        borderRadius: 2,
                                        boxShadow: isPozat
                                          ? "0 0 12px rgba(34,197,94,0.55)"
                                          : "none",
                                        border: isPozat
                                          ? "1px solid rgba(34,197,94,0.5)"
                                          : "1px solid transparent",
                                        transition:
                                          "transform 0.15s ease, box-shadow 0.15s ease",
                                        transform: draggableSnapshot.isDragging
                                          ? "rotate(1deg) scale(1.02)"
                                          : "none",
                                        boxShadow: draggableSnapshot.isDragging
                                          ? "0 15px 35px rgba(15,15,45,0.45)"
                                          : undefined,
                                      }}
                                      style={
                                        draggableProvided.draggableProps.style
                                      }
                                    >
                                      <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        sx={{ width: "100%" }}
                                      >
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            toggleSkuDetails(skuItem.sku)
                                          }
                                          sx={{
                                            color: "#E2E8F0",
                                          }}
                                        >
                                          {expandedSkus[skuItem.sku] ? (
                                            <KeyboardArrowUpIcon fontSize="inherit" />
                                          ) : (
                                            <KeyboardArrowDownIcon fontSize="inherit" />
                                          )}
                                        </IconButton>
                                        <IconButton
                                          size="small"
                                          {...draggableProvided.dragHandleProps}
                                          sx={{
                                            color: "#CBD5F5",
                                            cursor: "grab",
                                            "&:active": { cursor: "grabbing" },
                                          }}
                                        >
                                          <DragIndicatorIcon fontSize="inherit" />
                                        </IconButton>
                                        <Tooltip
                                          title={
                                            isPozat
                                              ? "Debifează SKU-ul"
                                              : "Marchează SKU-ul ca pozat"
                                          }
                                        >
                                          <span>
                                            <IconButton
                                              size="small"
                                              onClick={() =>
                                                handleToggleSkuPozat(
                                                  session,
                                                  skuItem
                                                )
                                              }
                                              sx={{
                                                color: isPozat
                                                  ? "#4ADE80"
                                                  : "#CBD5F5",
                                                backgroundColor: isPozat
                                                  ? "rgba(34,197,94,0.2)"
                                                  : "transparent",
                                                borderRadius: "50%",
                                              }}
                                            >
                                              {isPozat ? (
                                                <CheckCircleIcon
                                                  fontSize="inherit"
                                                  sx={{ color: "#ffffff" }}
                                                />
                                              ) : (
                                                <CheckCircleOutlineIcon fontSize="inherit" />
                                              )}
                                            </IconButton>
                                          </span>
                                        </Tooltip>
                                        <Typography
                                          variant="body2"
                                          sx={{ flex: 1 }}
                                        >
                                          {skuItem.sku}
                                          {skuItem.summary &&
                                          skuItem.issue_id?.startsWith("LEB")
                                            ? ` — ${skuItem.summary}`
                                            : ""}
                                        </Typography>
                                        {(skuItem.tasks?.length || 0) > 0 && (
                                          <Chip
                                            size="small"
                                            label={`${skuItem.tasks.length}`}
                                            sx={{
                                              background:
                                                "rgba(251,191,36,0.2)",
                                              color: "#FFEDD5",
                                              borderRadius: "999px",
                                              fontWeight: 600,
                                            }}
                                          />
                                        )}
                                        {skuItem.categorie_prod && (
                                          <Chip
                                            size="small"
                                            label={skuItem.categorie_prod}
                                            sx={{
                                              background:
                                                "rgba(248,250,252,0.1)",
                                              color: "#F8FAFC",
                                              borderRadius: "999px",
                                              border:
                                                "1px solid rgba(248,250,252,0.2)",
                                            }}
                                          />
                                        )}
                                        <IconButton
                                          color="error"
                                          size="small"
                                          onClick={() =>
                                            handleDeleteSku(
                                              session,
                                              skuItem.sku
                                            )
                                          }
                                        >
                                          <DeleteIcon fontSize="inherit" />
                                        </IconButton>
                                      </Stack>
                                      {expandedSkus[skuItem.sku] && (
                                        <Box
                                          sx={{
                                            mt: 1,
                                            p: 1.5,
                                            borderRadius: 2,
                                            background: "rgba(15,23,42,0.55)",
                                            width: "100%",
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1,
                                          }}
                                        >
                                          <Box
                                            onClick={() =>
                                              handleSkuPhotoFilename(skuItem)
                                            }
                                            sx={{
                                              p: 1,
                                              borderRadius: 2,
                                              background: isCopied
                                                ? "rgba(74,222,128,0.35)"
                                                : "rgba(34,197,94,0.15)",
                                              border: isCopied
                                                ? "1px solid rgba(34,197,94,0.8)"
                                                : "1px solid rgba(34,197,94,0.35)",
                                              cursor: "pointer",
                                              boxShadow: isCopied
                                                ? "0 0 16px rgba(34,197,94,0.55)"
                                                : "none",
                                              transform: isCopied
                                                ? "scale(1.02)"
                                                : "none",
                                              transition:
                                                "all 0.25s ease-in-out",
                                            }}
                                          >
                                            <Stack
                                              direction={{
                                                xs: "column",
                                                sm: "row",
                                              }}
                                              spacing={1}
                                              alignItems="center"
                                              justifyContent="space-between"
                                            >
                                              <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                sx={{
                                                  color: "#BBF7D0",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 0.5,
                                                }}
                                              >
                                                {photoFilename}
                                                {isCopied && (
                                                  <CheckCircleIcon
                                                    fontSize="inherit"
                                                    sx={{ color: "#ecfccb" }}
                                                  />
                                                )}
                                              </Typography>
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  color: "#D9F99D",
                                                  fontWeight: isCopied
                                                    ? 700
                                                    : 400,
                                                }}
                                              >
                                                {isCopied
                                                  ? "Copiat!"
                                                  : "Click pentru a copia numele fișierului"}
                                              </Typography>
                                            </Stack>
                                          </Box>
                                          {skuItem.tasks?.length ? (
                                            skuItem.tasks.map((task) => (
                                              <Stack
                                                key={task.id}
                                                direction="row"
                                                spacing={1}
                                                alignItems="center"
                                              >
                                                <Checkbox
                                                  size="small"
                                                  checked={!!task.done}
                                                  onChange={() =>
                                                    handleToggleSkuTask(
                                                      session,
                                                      skuItem,
                                                      task
                                                    )
                                                  }
                                                  sx={{
                                                    color: "#F8FAFC",
                                                    "&.Mui-checked": {
                                                      color: "#34D399",
                                                    },
                                                  }}
                                                />
                                                <Typography
                                                  variant="body2"
                                                  sx={{
                                                    flex: 1,
                                                    textDecoration: task.done
                                                      ? "line-through"
                                                      : "none",
                                                    opacity: task.done
                                                      ? 0.7
                                                      : 1,
                                                  }}
                                                >
                                                  {task.text}
                                                </Typography>
                                              </Stack>
                                            ))
                                          ) : (
                                            <Typography
                                              variant="body2"
                                              sx={{ opacity: 0.7 }}
                                            >
                                              Nu există task-uri pentru acest
                                              SKU.
                                            </Typography>
                                          )}
                                          <Stack
                                            direction={{
                                              xs: "column",
                                              sm: "row",
                                            }}
                                            spacing={1}
                                            alignItems={{
                                              xs: "stretch",
                                              sm: "center",
                                            }}
                                          >
                                            <TextField
                                              size="small"
                                              placeholder="Ex: De refăcut poza de profil"
                                              value={
                                                newTaskInputs[skuItem.sku] || ""
                                              }
                                              onChange={(e) =>
                                                handleTaskInputChange(
                                                  skuItem.sku,
                                                  e.target.value
                                                )
                                              }
                                              sx={{
                                                flex: 1,
                                                "& .MuiInputBase-root": {
                                                  background:
                                                    "rgba(15,23,42,0.5)",
                                                  color: "#F8FAFC",
                                                },
                                              }}
                                            />
                                            <Button
                                              variant="contained"
                                              color="primary"
                                              onClick={() =>
                                                handleAddSkuTask(
                                                  session,
                                                  skuItem
                                                )
                                              }
                                              disabled={
                                                !(
                                                  newTaskInputs[skuItem.sku] ||
                                                  ""
                                                ).trim()
                                              }
                                              sx={{ textTransform: "none" }}
                                            >
                                              Adaugă
                                            </Button>
                                          </Stack>
                                        </Box>
                                      )}
                                    </Box>
                                  )}
                                </Draggable>
                              );
                            })
                          ) : (
                            <Typography variant="body2" sx={{ opacity: 0.7 }}>
                              Nu există SKU-uri în această sesiune.
                            </Typography>
                          )}
                          {provided.placeholder}
                        </Box>
                      </Box>
                    )}
                  </Droppable>
                ))}
              </Box>
            </DragDropContext>
          )}
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        mx: "auto",
        position: "relative",
        overflowX: "auto",
        px: { xs: 1, md: 2 },
      }}
    >
      <div
        style={{
          padding: "10px",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search Summary..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <IconButton position="start">
                <SearchIcon />
              </IconButton>
            ),
          }}
        />

        {status === "AUTENTIFICARE" && (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveAltIcon />}
              onClick={handleExportPDF}
            >
              Export QC PDF
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setShowStudioProgramare(true)}
            >
              Programare Studio
            </Button>
          </Box>
        )}
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <Table
          size="small"
          sx={{
            minWidth: 650,
            "& td, & th": {
              padding: (theme) => theme.spacing(1, 1.5),
            },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Issue ID</TableCell>
              <TableCell
                sx={{
                  width: selectedTaskId
                    ? { xs: "auto", md: 200 }
                    : { xs: "auto", md: "auto" },
                  whiteSpace: selectedTaskId ? "nowrap" : "normal",
                  overflow: selectedTaskId ? "hidden" : "visible",
                  textOverflow: selectedTaskId ? "ellipsis" : "unset",
                }}
              >
                Summary
              </TableCell>
              {status === "TRANZIT" ? (
                <>
                  <TableCell>Status AWB GLS</TableCell>
                  <TableCell>Contract</TableCell>
                </>
              ) : status === "AUTENTIFICARE" ? (
                <>
                  <TableCell>QC</TableCell>
                  <TableCell>Fisa</TableCell>
                  <TableCell>Studio</TableCell>
                  <TableCell>Finanțat</TableCell>
                  <TableCell>Ciorna</TableCell>
                  <TableCell>Contract</TableCell>
                  <TableCell>Respins</TableCell>
                </>
              ) : status === "ONLINE" ? (
                <>
                  <TableCell>Status</TableCell>
                  <TableCell>Data</TableCell> {/* Coloana Data */}
                  <TableCell>Expira</TableCell> {/* Coloana Expira */}
                </>
              ) : (
                <>
                  <TableCell>Status</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Contactat</TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTasks.map((task) => (
              <TableRow
                key={task.issue_id}
                hover
                onClick={() => handleRowClick(task)}
                sx={{
                  cursor: "pointer",
                  "&:hover": { boxShadow: 3 },
                  backgroundColor:
                    selectedTaskId === task.issue_id
                      ? "rgba(25, 118, 210, 0.12)"
                      : "inherit",
                }}
              >
                <TableCell>
                  {task.issue_id}
                  {task.priceOption === "Propunere pret nou" && (
                    <Chip
                      label="Renegociere"
                      color="warning"
                      sx={{
                        marginLeft: 1,
                        fontSize: "0.75rem",
                        height: "20px",
                        padding: "0 6px",
                      }}
                    />
                  )}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: selectedTaskId ? "nowrap" : "normal",
                    overflow: selectedTaskId ? "hidden" : "visible",
                    textOverflow: selectedTaskId ? "ellipsis" : "unset",
                    maxWidth: selectedTaskId ? 200 : "none",
                    wordBreak: selectedTaskId ? "normal" : "break-word",
                  }}
                >
                  {selectedTaskId ? (
                    <Tooltip title={task.summary || ""} placement="top" arrow>
                      <span
                        style={{
                          display: "inline-block",
                          maxWidth: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          verticalAlign: "bottom",
                        }}
                      >
                        {formatSummaryForDisplay(task.summary)}
                      </span>
                    </Tooltip>
                  ) : (
                    task.summary
                  )}
                  {status === "ACCEPTAT" &&
                    isStocExternDa(task.customfield_10337) && (
                      <Chip
                        label="EXT"
                        sx={{
                          marginLeft: 1,
                          fontSize: "0.7rem",
                          height: "20px",
                          padding: "0 6px",
                          backgroundColor: "#FECACA",
                          color: "#991B1B",
                          fontWeight: 700,
                        }}
                      />
                    )}
                </TableCell>
                {status === "TRANZIT" ? (
                  <>
                    <TableCell>{task.awbStatusGls}</TableCell>
                    <TableCell>
                      {task.contractValid ? (
                        <CheckCircleIcon style={{ color: "green" }} />
                      ) : (
                        "Not available"
                      )}
                    </TableCell>
                  </>
                ) : status === "AUTENTIFICARE" ? (
                  <>
                    <TableCell>
                      {/* QC Status */}
                      {task.qcValid === "complete" ? (
                        <CheckCircleIcon style={{ color: "green" }} />
                      ) : task.qcValid === "partial" ? (
                        <WarningIcon style={{ color: "orange" }} />
                      ) : (
                        "Indisponibil"
                      )}
                      {/* CHIP-uri afișate doar dacă hasQualityCheck este false */}
                      {!task.hasQualityCheck &&
                        task.workdaysDiff >= 0 &&
                        task.workdaysDiff < 3 && (
                          <Chip
                            label="Suntem în timp cu QC"
                            color="success"
                            sx={{
                              marginLeft: 1,
                              fontSize: "0.75rem",
                              height: "20px",
                              padding: "0 6px",
                            }}
                          />
                        )}
                      {!task.hasQualityCheck && task.workdaysDiff >= 3 && (
                        <Chip
                          label={`QC depășit cu ${task.workdaysDiff} zile lucrătoare`}
                          color="error"
                          sx={{
                            marginLeft: 1,
                            fontSize: "0.75rem",
                            height: "20px",
                            padding: "0 6px",
                          }}
                        />
                      )}
                    </TableCell>

                    <TableCell>
                      {task.isPreCheck ? (
                        <HourglassEmptyIcon style={{ color: "blue" }} />
                      ) : task.prodStatusName === "CHECK" ? (
                        <CheckCircleOutlineIcon style={{ color: "#2563eb" }} />
                      ) : task.fisaValid ? (
                        <CheckCircleIcon style={{ color: "green" }} />
                      ) : (
                        "Indisponibil"
                      )}
                    </TableCell>
                    <TableCell>
                      {task.studioDone ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Studio"
                          color="success"
                          sx={{ fontWeight: 600 }}
                        />
                      ) : (
                        <WarningIcon style={{ color: "orange" }} />
                      )}
                    </TableCell>
                    <TableCell>
                      {task.investitiiFinantat ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Finanțat"
                          color="success"
                          sx={{ fontWeight: 700 }}
                        />
                      ) : (
                        <Tooltip title="Nefinanțat">
                          <Box
                            component="span"
                            sx={{
                              width: 18,
                              height: 18,
                              display: "inline-block",
                              borderRadius: "50%",
                              border: "2px solid #98a2b3",
                              background: "#f2f4f7",
                              verticalAlign: "middle",
                            }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {task.ciornaValid ? (
                        <CheckCircleIcon style={{ color: "green" }} />
                      ) : (
                        "Indisponibil"
                      )}
                    </TableCell>
                    <TableCell>
                      {task.contractValid ? (
                        <CheckCircleIcon style={{ color: "green" }} />
                      ) : (
                        "Not available"
                      )}
                    </TableCell>
                    <TableCell>
                      {task.respinsValid === "green" ? (
                        <CheckCircleIcon style={{ color: "green" }} />
                      ) : task.respinsValid === "orange" ? (
                        <WarningIcon style={{ color: "orange" }} />
                      ) : (
                        "Indisponibil"
                      )}
                    </TableCell>
                  </>
                ) : status === "ONLINE" ? (
                  <>
                    <TableCell>{task.status}</TableCell>
                    <TableCell>
                      {new Date(task.customfield_10078).toLocaleDateString(
                        "ro-RO"
                      )}
                    </TableCell>{" "}
                    {/* Data expirării */}
                    <TableCell>
                      <Chip
                        label={task.expiraStatus}
                        sx={{
                          backgroundColor:
                            task.expiraStatus === "Curând"
                              ? "#4CAF50" // Verde
                              : task.expiraStatus === "Astăzi"
                                ? "#FF9800" // Portocaliu
                                : "#F44336", // Roșu
                          color: "#fff",
                          fontWeight: "bold",
                          padding: "0 10px",
                          fontSize: "0.875rem",
                        }}
                      />
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{task.status}</TableCell>
                    <TableCell>
                      {new Date(task.created_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {console.log(
                        "customfield_10185 for task:",
                        task.customfield_10185
                      )}
                      <Chip
                        label={task.customfield_10185 || "Necontactat"}
                        color={getChipColor(
                          task.customfield_10185 || "Necontactat"
                        )}
                      />
                    </TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
};

export default TaskTable;
